/**
 * Order Detail — /marketplace/orders/{order_id}
 *
 * Shows: product header, order id/date, amount & payment method, delivery address,
 * status timeline (Confirmed → Shipped → Delivered), seller row + Message Seller,
 * Leave a Review button (when delivered), Request Return (when delivered within 7d
 * and category ≠ supplements), return status banner once requested.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  Truck,
  PackageCheck,
  ShoppingBag,
  MessageCircle,
  Undo2,
  X,
  Camera,
} from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import ReviewSheet from "./ReviewSheet";
import BottomNav from "../../components/layout/BottomNav";
import { toast } from "sonner";

const RETURN_REASONS = [
  { id: "item_not_as_described", label: "Item not as described" },
  { id: "wrong_item", label: "Wrong item received" },
  { id: "item_damaged", label: "Item damaged" },
  { id: "changed_my_mind", label: "Changed my mind" },
];

const NON_RETURNABLE_CATEGORIES = new Set(["supplements"]);
const RETURN_WINDOW_DAYS = 7;

export default function OrderDetail() {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [order, setOrder] = useState(null);
  const [returnInfo, setReturnInfo] = useState(null);
  const [myReview, setMyReview] = useState(null);
  const [reviewSheet, setReviewSheet] = useState(false);
  const [returnSheet, setReturnSheet] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/marketplace/orders/${order_id}`);
      setOrder(data);
    } catch {
      toast.error("Could not load order");
    }
    try {
      const { data: ret } = await apiClient.get(`/marketplace/orders/${order_id}/return`);
      setReturnInfo(ret?.return || null);
    } catch {
      /* no return — fine */
    }
    try {
      const { data: mine } = await apiClient.get(
        `/marketplace/reviews/order/${order_id}/mine`
      );
      setMyReview(mine?.review || null);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, [order_id]);

  const isBuyer = order && user?.user_id === order.buyer_id;
  const firstItem = order?.items?.[0];

  const returnEligible = useMemo(() => {
    if (!order || !firstItem) return false;
    if (order.status !== "delivered") return false;
    if (!order.delivered_at) return false;
    const delivered = new Date(order.delivered_at);
    const days = (Date.now() - delivered.getTime()) / (1000 * 60 * 60 * 24);
    if (days > RETURN_WINDOW_DAYS) return false;
    const nonReturnable = (order.items || []).some((it) =>
      NON_RETURNABLE_CATEGORIES.has(it.category)
    );
    return !nonReturnable;
  }, [order, firstItem]);

  const messageSeller = async () => {
    if (!firstItem) return;
    try {
      const { data } = await apiClient.post("/marketplace/threads", {
        product_id: firstItem.product_id,
      });
      navigate(`/inbox?tab=marketplace&thread=${data.thread_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open thread");
    }
  };

  if (!order) {
    return (
      <div className="app-page min-h-screen" data-testid="order-detail-loading">
        <div className="p-10 text-center text-ink-soft">Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="app-page min-h-screen pb-32"
      data-testid="order-detail"
      style={{ background: "#F4F3F1" }}
    >
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="order-detail-back"
            className="w-10 h-10 grid place-items-center"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-barlow-cond font-extrabold text-[22px]">Order details</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Product header */}
        <button
          type="button"
          onClick={() => firstItem && navigate(`/marketplace/product/${firstItem.product_id}`)}
          className="w-full text-left bg-white border border-line2 rounded-[14px] p-3 flex items-center gap-3"
          data-testid="order-product-header"
        >
          <div className="w-16 h-16 rounded-[10px] overflow-hidden bg-canvas2 shrink-0">
            {firstItem?.photo && (
              <img src={firstItem.photo} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold line-clamp-2">{firstItem?.title}</div>
            <div className="text-[11px] text-ink-tertiary mt-1">
              Qty {firstItem?.quantity || 1}
              {order.items?.length > 1 && ` · +${order.items.length - 1} more`}
            </div>
          </div>
        </button>

        {/* Meta */}
        <section
          className="bg-white border border-line2 rounded-[14px] p-4 space-y-2"
          data-testid="order-meta"
        >
          <Row label="Order ID" value={`#${order.order_id.slice(-10)}`} />
          <Row label="Placed" value={formatDate(order.created_at)} />
          <Row
            label="Amount paid"
            value={`₹${(order.amount_inr || 0).toLocaleString("en-IN")}`}
            bold
          />
          <Row
            label="Payment"
            value={order.razorpay_payment_id ? "Razorpay · Online" : "Pending"}
          />
          {order.fitpoints_used > 0 && (
            <Row label="FitPoints used" value={`${order.fitpoints_used} FP (−₹${order.discount_inr || 0})`} />
          )}
        </section>

        {/* Delivery address */}
        {order.delivery_address && (
          <section
            className="bg-white border border-line2 rounded-[14px] p-4"
            data-testid="order-address"
          >
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-ink-tertiary">
              Delivery address
            </div>
            <div className="mt-1 text-[14px] font-semibold">
              {order.delivery_address.full_name}
            </div>
            <div className="text-[13px] text-ink-soft mt-0.5">
              {order.delivery_address.address_line}, {order.delivery_address.city} ·{" "}
              {order.delivery_address.pincode}
            </div>
            <div className="text-[13px] text-ink-soft">{order.delivery_address.phone}</div>
          </section>
        )}

        {/* Status timeline */}
        <StatusTimeline order={order} />

        {/* Return banner */}
        {returnInfo && (
          <section
            className="bg-white border border-line2 rounded-[14px] p-4"
            data-testid="return-status"
            style={{ borderLeft: "3px solid #00B0D5" }}
          >
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: "#00B0D5" }}>
              Return · {returnInfo.status.replace("_", " ")}
            </div>
            <div className="text-[13px] mt-1 font-semibold">
              {RETURN_REASONS.find((r) => r.id === returnInfo.reason)?.label || returnInfo.reason}
            </div>
            {returnInfo.status === "pending" && (
              <div className="text-[12px] text-ink-soft mt-1">
                Seller has until {formatDate(returnInfo.seller_deadline_at)} to respond.
              </div>
            )}
            {returnInfo.status === "admin_review" && (
              <div className="text-[12px] text-ink-soft mt-1">
                Admin is reviewing this request — we'll update you by email.
              </div>
            )}
            {returnInfo.status === "accepted" && (
              <div className="text-[12px] text-ink-soft mt-1">
                Refund initiated. It should reflect in 5-7 working days.
              </div>
            )}
          </section>
        )}

        {/* Seller row + actions */}
        <section
          className="bg-white border border-line2 rounded-[14px] p-4"
          data-testid="order-seller"
        >
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-ink-tertiary">
            Seller
          </div>
          <div className="mt-1 text-[14px] font-semibold">{firstItem?.seller_name || "Seller"}</div>

          <div className="mt-3 space-y-2">
            <button
              onClick={messageSeller}
              data-testid="order-message-seller"
              className="w-full h-11 rounded-pill border border-line2 text-[13px] font-semibold flex items-center justify-center gap-2"
              style={{ borderRadius: 100 }}
            >
              <MessageCircle size={16} /> Message Seller
            </button>

            {isBuyer && order.status === "delivered" && !myReview && (
              <button
                onClick={() => setReviewSheet(true)}
                data-testid="order-leave-review"
                className="w-full h-11 rounded-pill text-[13px] font-bold flex items-center justify-center gap-2"
                style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
              >
                <ShoppingBag size={16} /> Leave a Review
              </button>
            )}

            {isBuyer && returnEligible && !returnInfo && (
              <button
                onClick={() => setReturnSheet(true)}
                data-testid="order-request-return"
                className="w-full h-11 rounded-pill border border-line2 text-[13px] font-semibold flex items-center justify-center gap-2"
                style={{ borderRadius: 100 }}
              >
                <Undo2 size={16} /> Request Return
              </button>
            )}
          </div>
        </section>
      </main>

      {reviewSheet && (
        <ReviewSheet
          orderId={order.order_id}
          onClose={() => setReviewSheet(false)}
          onDone={() => load()}
        />
      )}
      {returnSheet && (
        <ReturnRequestSheet
          orderId={order.order_id}
          onClose={() => setReturnSheet(false)}
          onDone={() => load()}
        />
      )}

      <BottomNav />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Timeline
// ─────────────────────────────────────────────────────────────
function StatusTimeline({ order }) {
  const steps = [
    {
      id: "confirmed",
      label: "Confirmed",
      icon: CheckCircle2,
      date: order.paid_at || order.created_at,
      done: ["confirmed", "shipped", "delivered", "return_requested", "return_admin_review", "refunded"].includes(
        order.status
      ),
    },
    {
      id: "shipped",
      label: "Shipped",
      icon: Truck,
      date: order.shipped_at,
      done: ["shipped", "delivered", "return_requested", "return_admin_review", "refunded"].includes(order.status),
    },
    {
      id: "delivered",
      label: "Delivered",
      icon: PackageCheck,
      date: order.delivered_at,
      done: ["delivered", "return_requested", "return_admin_review", "refunded"].includes(order.status),
    },
  ];

  return (
    <section
      className="bg-white border border-line2 rounded-[14px] p-4"
      data-testid="status-timeline"
    >
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-ink-tertiary mb-3">
        Status
      </div>
      <div className="flex items-start gap-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="flex-1 flex flex-col items-center" data-testid={`tl-${s.id}`}>
              <div
                className="w-9 h-9 rounded-full grid place-items-center"
                style={{
                  background: s.done ? "#00D4FF" : "#ECEAE7",
                  color: s.done ? "#000" : "#A09C97",
                }}
              >
                <Icon size={16} />
              </div>
              <div
                className="text-[11px] font-semibold mt-1.5 text-center"
                style={{ color: s.done ? "#1A1A1A" : "#A09C97" }}
              >
                {s.label}
              </div>
              {s.date && s.done && (
                <div className="text-[10px] text-ink-tertiary mt-0.5 text-center">
                  {formatDate(s.date, { short: true })}
                </div>
              )}
              {i < steps.length - 1 && (
                <div className="hidden" /> /* connector simplified; icons already convey state */
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Return Request Sheet
// ─────────────────────────────────────────────────────────────
function ReturnRequestSheet({ orderId, onClose, onDone }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const handleImage = (file) => {
    if (!file) return;
    if (photos.length >= 2) return toast.error("Max 2 photos");
    const reader = new FileReader();
    reader.onload = () => setPhotos((p) => [...p, reader.result]);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!reason) return toast.error("Select a reason");
    setBusy(true);
    try {
      await apiClient.post(`/marketplace/orders/${orderId}/request-return`, {
        reason,
        photos,
        note: note.trim() || null,
      });
      toast.success("Return requested");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-end"
      onClick={onClose}
      data-testid="return-sheet"
    >
      <div
        className="w-full bg-white rounded-t-[24px] p-5 max-w-2xl mx-auto"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-barlow-cond font-extrabold text-[22px]">Request Return</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 grid place-items-center rounded-full bg-canvas2"
          >
            <X size={14} />
          </button>
        </div>

        <div className="mt-4 space-y-2" data-testid="return-reasons">
          {RETURN_REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setReason(r.id)}
              data-testid={`return-reason-${r.id}`}
              className="w-full h-11 px-4 rounded-[12px] border text-[13px] text-left font-semibold"
              style={{
                borderColor: reason === r.id ? "#00D4FF" : "#E2E0DC",
                background: reason === r.id ? "rgba(0,212,255,0.08)" : "#fff",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        {(reason === "item_damaged" || reason === "wrong_item") && (
          <div className="mt-3" data-testid="return-photos">
            <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mb-1.5">
              Photos (optional, max 2)
            </div>
            <div className="flex gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-[10px] overflow-hidden">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                    aria-label="Remove"
                    className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-black/60 text-white"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
              {photos.length < 2 && (
                <label
                  className="w-20 h-20 rounded-[10px] border-2 border-dashed grid place-items-center cursor-pointer"
                  style={{ borderColor: "#E2E0DC" }}
                  data-testid="return-add-photo"
                >
                  <Camera size={20} className="text-ink-tertiary" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImage(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          placeholder="Add a note (optional)"
          className="mt-3 w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          style={{ minHeight: 70 }}
        />

        <button
          onClick={submit}
          disabled={busy || !reason}
          data-testid="return-submit"
          className="w-full mt-3 h-12 rounded-pill disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
        >
          {busy ? "Submitting…" : "Submit Return Request"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-ink-soft">{label}</span>
      <span className={bold ? "font-barlow-cond font-black text-[16px]" : "font-semibold"}>
        {value}
      </span>
    </div>
  );
}

function formatDate(iso, opts = {}) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (opts.short) {
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
